// Componentes UI compartidos. Cada vez que un patrón se repite ≥2 veces en
// distintas páginas, vive aquí en lugar de duplicarse inline.
//
// Reglas:
// - Sin lógica de negocio. Solo presentación + props.
// - Sin colores hardcoded — usar variables CSS (var(--pu), var(--tx)…)
// - Props mínimos. Si necesitas mucha config, crea un wrapper específico.

import { useState, useEffect, useRef } from '../common.jsx';

// ── NewFeatureBanner ─────────────────────────────────────────────────────────
// Banner "💡 Nuevo" para discoverability de features escondidas (ej. click en
// header de mes en Cobros, tab Equipo vs Objetivo, chips editables). Se muestra
// hasta que el user pulsa "Entendido" o el botón ×. La dismissión se persiste
// en user.dismissedFeatures[] via POST /api/users/:id/dismiss-feature.
//
// featureId debe versionarse (cobros-month-drawer-v1, v2…) para re-mostrar
// banners cuando una feature cambia significativamente. Si activeUser no está
// disponible, no se muestra (no podemos persistir la dismissión).
export function NewFeatureBanner({ featureId, activeUser, title, body, cta=null, onCta=null }) {
  const initiallyDismissed = (activeUser?.dismissedFeatures||[]).includes(featureId);
  const [visible, setVisible] = useState(!initiallyDismissed && !!activeUser?.id);
  useEffect(() => {
    // Si activeUser cambia (login/impersonate), re-evaluamos.
    setVisible(!(activeUser?.dismissedFeatures||[]).includes(featureId) && !!activeUser?.id);
  }, [activeUser?.id, activeUser?.dismissedFeatures, featureId]);

  const dismiss = async () => {
    setVisible(false); // instant feedback
    if (!activeUser?.id) return;
    try {
      await fetch(`/api/users/${activeUser.id}/dismiss-feature`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ featureId })
      });
      // Mutar el activeUser local para que el banner no reaparezca si el
      // user navega entre páginas durante esta sesión sin recargar.
      if (Array.isArray(activeUser.dismissedFeatures)) {
        if (!activeUser.dismissedFeatures.includes(featureId)) activeUser.dismissedFeatures.push(featureId);
      } else {
        activeUser.dismissedFeatures = [featureId];
      }
    } catch (e) {
      // Silencioso: la UX local ya marca como cerrado. El próximo reload lo
      // mostrará otra vez si el POST falló, no es crítico.
    }
  };

  if (!visible) return null;
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:12,
      background:'var(--pu-bgl)', border:'1px solid var(--pu)',
      borderRadius:8, padding:'10px 14px', marginBottom:14,
      animation:'fadeInBanner .25s ease'
    }}>
      <style>{`@keyframes fadeInBanner{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <span style={{fontSize:18, flexShrink:0}}>💡</span>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:10, fontWeight:700, color:'var(--pu)', textTransform:'uppercase', letterSpacing:'.5px'}}>Nuevo</div>
        <div style={{fontSize:13, color:'var(--tx)', marginTop:2, lineHeight:1.4}}>
          <strong>{title}</strong>{body && <span style={{color:'var(--tx2)'}}> · {body}</span>}
        </div>
      </div>
      {cta && onCta && (
        <button className="btn btn-pu btn-sm" onClick={() => { onCta(); dismiss(); }}>
          {cta}
        </button>
      )}
      <button onClick={dismiss} style={{
        background:'transparent', border:'1px solid var(--pu)40', color:'var(--pu)',
        borderRadius:6, padding:'5px 12px', fontSize:12, fontWeight:600, cursor:'pointer'
      }}>Entendido</button>
      <button onClick={dismiss} title="Cerrar" style={{
        background:'transparent', border:'none', color:'var(--tx-mu)',
        cursor:'pointer', fontSize:16, padding:'0 4px', lineHeight:1
      }}>×</button>
    </div>
  );
}

// ── TooltipIcon ──────────────────────────────────────────────────────────────
// Icono ⓘ con popover hover-only. position:fixed para no quedar clipado por
// overflow:hidden del card padre. Stop propagation en click para no disparar
// handlers del padre (típico: card clickable que abre drill-down) pero NO
// abre — el popover solo aparece en hover.
//
// Decisión: antes había modo 'pinned' por click. Lo quitamos a petición —
// se prefiere la UX de hover puro (más rápido, sin estado pegado).
export function TooltipIcon({ text }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const computePos = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const w = 320;
    let left = r.right - w;
    if (left < 12) left = 12;
    if (left + w > window.innerWidth - 12) left = window.innerWidth - w - 12;
    setPos({ top: r.bottom + 8, left });
  };
  return (
    <span ref={ref}
      onMouseEnter={() => { computePos(); setShow(true); }}
      onMouseLeave={() => setShow(false)}
      onClick={e => e.stopPropagation()}
      style={{
        position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'help', fontSize: 11, color: 'var(--tx3)', lineHeight: 1, fontWeight: 700,
        border: '1px solid var(--bds)', borderRadius: '50%', width: 16, height: 16, flexShrink: 0,
        background: show ? 'var(--bg)' : 'transparent'
      }}>
      i
      {show && (
        <span style={{
          position: 'fixed', left: pos.left, top: pos.top,
          zIndex: 9999, width: 320, maxWidth: 'calc(100vw - 24px)',
          background: 'var(--tx)', color: 'var(--wh)',
          padding: '12px 14px', borderRadius: 8,
          fontSize: 12, fontWeight: 400, lineHeight: 1.55, letterSpacing: 0,
          textTransform: 'none', boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          pointerEvents: 'none',
          whiteSpace: 'normal', textAlign: 'left'
        }}>{text}</span>
      )}
    </span>
  );
}

// ── KpiCard ──────────────────────────────────────────────────────────────────
// Tarjeta de KPI con label, valor, sub-texto opcional, tooltip opcional,
// badge YoY opcional, click handler opcional con hover-lift.
//
// Props:
// - label, value, sub: textos
// - alert: pinta el valor en rojo si true
// - onClick: añade cursor pointer + transform en hover
// - tooltip: muestra ⓘ con popover
// - yoyPct: añade badge ↑/↓X%
// - yoyLabel: tooltip del badge
// - yoyInverse: invierte colores (para métricas donde menos es mejor: bajas/impagos)
export function KpiCard({ label, value, sub, alert = false, onClick = null, tooltip = null, yoyPct = null, yoyLabel = null, yoyInverse = false }) {
  return (
    <div className="card" style={{
      padding: '16px 18px',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform .12s, box-shadow .12s',
      position: 'relative'
    }}
      onClick={onClick || undefined}
      onMouseEnter={onClick ? e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.06)'; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; } : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx-mu)', textTransform: 'uppercase', letterSpacing: '.5px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
        {tooltip && <TooltipIcon text={tooltip} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: alert ? 'var(--er)' : 'var(--tx)', letterSpacing: '-.5px' }}>{value}</div>
        {yoyPct != null && Math.abs(yoyPct) >= 0.5 && (() => {
          const isPositive = yoyInverse ? yoyPct < 0 : yoyPct > 0;
          return (
            <span title={yoyLabel || ''} style={{
              display: 'inline-flex', alignItems: 'center', gap: 2,
              fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
              background: isPositive ? 'var(--gr-bg)' : 'var(--er-bg)',
              color: isPositive ? 'var(--gr-d)' : 'var(--er)'
            }}>{yoyPct > 0 ? '↑' : '↓'}{Math.abs(yoyPct).toFixed(0)}%</span>
          );
        })()}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--tx-mu)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── KpiSkeleton ─────────────────────────────────────────────────────────────
// Esqueleto con shimmer (CSS .sk-line en index.html). Ocupa el mismo espacio
// que un KpiCard para evitar saltos de layout al cargar.
export function KpiSkeleton() {
  return (
    <div className="card" style={{ padding: '16px 18px' }}>
      <div className="sk-line" style={{ width: '60%', height: 10, marginBottom: 8 }} />
      <div className="sk-line" style={{ width: '80%', height: 22, marginBottom: 6 }} />
      <div className="sk-line" style={{ width: '45%', height: 11 }} />
    </div>
  );
}

// ── ModalShell ──────────────────────────────────────────────────────────────
// Estructura común de modal: overlay centrado + click-outside para cerrar +
// header con título + close button + cuerpo + footer.
//
// Uso:
//   <ModalShell title="..." onClose={...} footer={<>...</>}>
//     ...contenido...
//   </ModalShell>
//
// Para casos especiales (custom header/footer) el caller puede meter <div
// className="mh|mb|mf"> directamente como children y omitir title/footer.
export function ModalShell({ title, onClose, footer, children, maxWidth = 480, closeOnBackdrop = true }) {
  return (
    <div className="ov" onClick={closeOnBackdrop ? onClose : undefined}>
      <div className="modal ani" style={{ maxWidth, width: '95%' }} onClick={e => e.stopPropagation()}>
        {title != null && (
          <div className="mh">
            <h3>{title}</h3>
            {onClose && <button className="btn btn-gh btn-ic" onClick={onClose} aria-label="Cerrar">✕</button>}
          </div>
        )}
        <div className="mb">{children}</div>
        {footer && <div className="mf">{footer}</div>}
      </div>
    </div>
  );
}

// ── DataTable ───────────────────────────────────────────────────────────────
// Tabla con cabecera + filas + footer opcional. Pensada para reportes y
// rankings. Columns es array de { key, header, format?, align?, width? }.
//
// Props:
// - columns: definición de columnas
// - rows: array de objetos
// - footer: objeto con totales (opcional, mismas keys que columns)
// - onRowClick(row): si presente, fila clickable
// - emptyText: texto cuando rows está vacío
export function DataTable({ columns, rows, footer = null, onRowClick = null, emptyText = 'Sin datos' }) {
  if (!rows || rows.length === 0) {
    return <div style={{ padding: '24px 18px', color: 'var(--tx-mu)', fontSize: 12, textAlign: 'center' }}>{emptyText}</div>;
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: 'var(--bg)' }}>
          {columns.map((c, i) => (
            <th key={i} style={{
              textAlign: c.align || (i === 0 ? 'left' : 'right'),
              padding: '8px 12px', fontSize: 11, fontWeight: 600, color: 'var(--tx-mu)',
              textTransform: 'uppercase', letterSpacing: '.4px',
              borderBottom: '1px solid var(--bds)', width: c.width || 'auto'
            }}>{c.header || c.key}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.id || i}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            style={{
              borderBottom: '1px solid var(--bds)',
              cursor: onRowClick ? 'pointer' : 'default'
            }}>
            {columns.map((c, j) => (
              <td key={j} style={{
                textAlign: c.align || (j === 0 ? 'left' : 'right'),
                padding: '8px 12px', fontSize: 12,
                fontWeight: j === 0 ? 600 : 500,
                color: 'var(--tx)'
              }}>{c.format ? c.format(r[c.key], r) : r[c.key]}</td>
            ))}
          </tr>
        ))}
      </tbody>
      {footer && (
        <tfoot>
          <tr style={{ background: 'var(--bg)' }}>
            {columns.map((c, j) => (
              <td key={j} style={{
                textAlign: c.align || (j === 0 ? 'left' : 'right'),
                padding: '8px 12px', fontSize: 11, fontWeight: 700,
                color: 'var(--tx2)', borderTop: '1px solid var(--bds)'
              }}>{c.format ? c.format(footer[c.key], footer) : footer[c.key]}</td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  );
}
